import { eventTracker } from 'app/modules/Segment';
import {
  buildJusfyEventsContext,
  buildJusfyEventsProperties,
} from 'app/modules/Segment/buildJusfyEvent';
import { FeatureEnum, TContext, TProperties } from 'app/modules/Segment/types';

import type { NewTierPlan } from 'app/hooks/useSubscriptionPlans/getNewTierPlans';
import type { ProductSelected } from 'app/hooks/useSubscriptionPlans/selectNewTierPlan';
import type { PlanExplorationOrigin } from 'app/hooks/usePlanExploration/types';
import type { CurrentTier } from 'app/hooks/useSubscriptionPlans/resolveCurrentTier';

export const EVENTS = {
  EXPLORE_PLANS_MODAL_VIEWED: 'explore_plans_modal_viewed',
  EXPLORE_PLANS_PLAN_SELECTED: 'explore_plans_plan_selected',
  COMPARE_PLANS_MODAL_VIEWED: 'compare_plans_modal_viewed',
  CHECKOUT_MODAL_VIEWED: 'checkout_modal_viewed',
  CHECKOUT_SUBSCRIBE_CLICKED: 'checkout_subscribe_clicked',
  PERSONALIZED_PLAN_CARD_VIEWED: 'personalized_plan_card_viewed',
  PERSONALIZED_PLAN_CARD_CLICKED: 'personalized_plan_card_clicked',
} as const;

export type PersonalizedPlanCardScreen = 'explore_plans' | 'credits_checkout';

type PersonalizedPlanCardTrackingInput = {
  screen: PersonalizedPlanCardScreen;
  currentTier: CurrentTier;
  currentPlan: string;
};

// '-' é o placeholder de "sem plano" de useSubscriptionStatus/usePlansCatalog; o
// contrato deste evento não aceita propriedade nula.
const resolvePersonalizedPlanCardCurrentPlan = (currentPlan: string): string =>
  currentPlan === '-' ? 'sem_plano' : currentPlan;

function buildBaseProperties(
  event: string,
  eventMeaning: string,
  relevantUserActivity: boolean,
  extraProperties: Partial<TProperties> = {},
): TProperties {
  return buildJusfyEventsProperties({
    event,
    feature: FeatureEnum.SUBSCRIPTION_MANAGEMENT,
    eventMeaning,
    relevantUserActivity,
    extraProperties,
  });
}

export function trackExplorePlansModalViewed(origin?: PlanExplorationOrigin): void {
  const event = EVENTS.EXPLORE_PLANS_MODAL_VIEWED;
  const context: TContext = buildJusfyEventsContext({ component: 'modal-component' });

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      'Disparado quando o modal "Explorar planos" é exibido ao clicar em "Explorar outros planos".',
      false,
      origin ? { entry_point: origin } : {},
    ),
    context,
  });
}

export function trackComparePlansModalViewed(): void {
  const event = EVENTS.COMPARE_PLANS_MODAL_VIEWED;
  const context: TContext = buildJusfyEventsContext({ component: 'modal-component' });

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      'Disparado quando o modal "Comparar todos os planos" é exibido ao clicar em "Comparar todos os planos" no modal "Explorar planos".',
      false,
    ),
    context,
  });
}

export function trackCheckoutModalViewed(): void {
  const event = EVENTS.CHECKOUT_MODAL_VIEWED;
  const context: TContext = buildJusfyEventsContext({ component: 'modal-component' });

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      'Disparado quando o modal "Checkout" é exibido ao clicar em "Assinar" num plano.',
      false,
    ),
    context,
  });
}

export function trackCheckoutSubscribeClicked(planType: ProductSelected['plan_type']): void {
  const event = EVENTS.CHECKOUT_SUBSCRIBE_CLICKED;
  const context: TContext = buildJusfyEventsContext({ component: 'button-component' });

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      'Disparado ao clicar em "Assinar agora" no modal "Checkout", antes de processar o pagamento.',
      true,
      { plan_type: planType },
    ),
    context,
  });
}

export function trackPersonalizedPlanCardViewed({
  screen,
  currentTier,
  currentPlan,
}: PersonalizedPlanCardTrackingInput): void {
  const event = EVENTS.PERSONALIZED_PLAN_CARD_VIEWED;
  const context: TContext = buildJusfyEventsContext({ component: 'card-component' });

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      'Disparado quando o card do plano Personalizado é exibido, no Explorar planos ou na compra de créditos.',
      false,
      {
        screen,
        current_tier: currentTier,
        current_plan: resolvePersonalizedPlanCardCurrentPlan(currentPlan),
      },
    ),
    context,
  });
}

export function trackPersonalizedPlanCardClicked({
  screen,
  currentTier,
  currentPlan,
}: PersonalizedPlanCardTrackingInput): void {
  const event = EVENTS.PERSONALIZED_PLAN_CARD_CLICKED;
  const context: TContext = buildJusfyEventsContext({ component: 'card-component' });

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      'Disparado ao clicar em "Falar com especialista" no card do plano Personalizado, antes de abrir o WhatsApp.',
      true,
      {
        screen,
        current_tier: currentTier,
        current_plan: resolvePersonalizedPlanCardCurrentPlan(currentPlan),
      },
    ),
    context,
  });
}

export function trackExplorePlansPlanSelected(planKey: NewTierPlan['key']): void {
  const event = EVENTS.EXPLORE_PLANS_PLAN_SELECTED;
  const context: TContext = buildJusfyEventsContext({ component: 'card-component' });

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      'Disparado ao clicar em "Assinar" num card do modal "Explorar planos", antes de cair no checkout.',
      true,
      { plan_key: planKey },
    ),
    context,
  });
}
